import type {Content, KeyTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import type {FC} from 'react'
import {useCallback, useMemo, useState} from 'react'
import {ToastProvider} from 'react-toast-notifications'
import {useTranslate} from 'src/common/i18n/useTranslate'
import type {Context} from 'src/common/types'
import {DownloadTemplateModal} from './components/DownloadTemplateModal'
import {useDownloadTemplateLinks} from './hooks/useDownloadTemplateLinks'
import type {DownloadTemplateCopy} from './types'

export type DownloadTemplateProps = SliceComponentProps<Content.DownloadTemplateSlice>

const textOr = (field: KeyTextField, fallback: string): string => (isFilled.keyText(field) ? field : fallback)

const DownloadTemplate: FC<DownloadTemplateProps & {context: Context}> = ({
  slice,
  context: {pageUid, buttonsData},
  index: componentIndex,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const translate = useTranslate()

  const openModal = useCallback(() => setIsModalOpen(true), [])
  const closeModal = useCallback(() => setIsModalOpen(false), [])

  const isFormEnabled = !slice.primary.download_without_form
  useDownloadTemplateLinks(isFormEnabled ? buttonsData : undefined, openModal)

  const copy: DownloadTemplateCopy = useMemo(
    () => ({
      heading: textOr(slice.primary.popup_heading, translate('downloadTemplate.heading')),
      description: textOr(slice.primary.popup_description, translate('downloadTemplate.description')),
      submitLabel: textOr(slice.primary.submit_button_label, translate('downloadTemplate.submit')),
      submittingLabel: translate('downloadTemplate.submitting'),
      generatorPrompt: textOr(slice.primary.generator_prompt, translate('downloadTemplate.generatorPrompt')),
      generatorLinkLabel: textOr(slice.primary.generator_link_label, translate('downloadTemplate.generatorLink')),
      closeLabel: translate('downloadTemplate.close'),
    }),
    [slice.primary, translate],
  )

  if (!isFormEnabled) {
    return null
  }

  const trackingLocation = `component-${componentIndex}-download-template`

  return (
    <ToastProvider autoDismiss={5000}>
      <DownloadTemplateModal
        isOpen={isModalOpen}
        onClose={closeModal}
        previewImage={slice.primary.template_preview_image}
        pdfLink={slice.primary.template_pdf}
        hubspotFormId={isFilled.keyText(slice.primary.hubspot_form_id) ? slice.primary.hubspot_form_id : null}
        pageUid={pageUid || 'unknown'}
        trackingLocation={trackingLocation}
        privacyNotice={slice.primary.privacy_notice}
        copy={copy}
      />
    </ToastProvider>
  )
}

export default DownloadTemplate
